import { format, startOfDay } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RotateCcw,
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLanguage } from 'app/pages/App/slice/selectors';
import { cn } from '@/lib/utils';

type DatePickerProps = {
  value?: Date | { start: Date; end: Date };
  onChange?: (date: Date | { start: Date; end: Date } | string | null) => void;
  className?: string;
  maxDate?: Date;
  minDate?: Date;
  mode?: 'single' | 'range';
  onClose?: () => void;
  isShowBtnReset?: boolean;
  isShowBtnFooter?: boolean;
  pickerMode?: 'days' | 'months' | 'years';
  showLunar?: boolean;
  disabled?: (date: Date) => boolean;
};

const getLunarDayLabel = (_date: Date) => '';

export default function DatePicker({
  value,
  onChange,
  className,
  maxDate,
  mode = 'single',
  onClose,
  isShowBtnReset = true,
  minDate,
  isShowBtnFooter,
  pickerMode = 'days',
  showLunar = false,
  disabled,
}: DatePickerProps) {
  const { t } = useTranslation();
  const language = useSelector(selectLanguage);
  const locale = language?.startsWith('vi') ? vi : enUS;
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    if (mode === 'single' && value instanceof Date) return value;
    if (mode === 'range' && value && typeof value === 'object' && 'start' in value) return value.start;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (maxDate && maxDate < today) return maxDate;
    if (minDate && minDate > today) return minDate;
    return new Date();
  });

  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (mode === 'single' && value instanceof Date) return value;
    return null;
  });
  const [selectedRange, setSelectedRange] = useState<{ start: Date | null; end: Date | null }>(() => {
    if (mode === 'range' && value && typeof value === 'object' && 'start' in value && 'end' in value) {
      return { start: value.start, end: value.end };
    }
    return { start: null, end: null };
  });
  const [view, setView] = useState<'days' | 'months' | 'years'>(pickerMode);
  const [yearRangeStart, setYearRangeStart] = useState(Math.floor(currentDate.getFullYear() / 12) * 12);

  const monthNamesLong = Array.from({ length: 12 }, (_, i) => format(new Date(2000, i, 1), 'MMMM', { locale }));
  const dayNames = Array.from({ length: 7 }, (_, i) => format(new Date(2000, 0, i + 3), 'EEEEEE', { locale }));

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const handlePrevious = () => {
    if (view === 'days') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(1);
        newDate.setMonth(newDate.getMonth() - 1);
        if (minDate && newDate <= minDate) newDate.setMonth(minDate.getMonth());
        return newDate;
      });
    } else if (view === 'months') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setFullYear(newDate.getFullYear() - 1);
        return newDate;
      });
    } else {
      setYearRangeStart(yearRangeStart - 12);
    }
  };

  const handleNext = () => {
    if (view === 'days') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(1);
        newDate.setMonth(newDate.getMonth() + 1);
        return newDate;
      });
    } else if (view === 'months') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setFullYear(newDate.getFullYear() + 1);
        return newDate;
      });
    } else {
      setYearRangeStart(yearRangeStart + 12);
    }
  };

  const handlePreviousYear = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(1);
      newDate.setFullYear(newDate.getFullYear() - 1);
      return newDate;
    });
  };

  const handleNextYear = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(1);
      newDate.setFullYear(newDate.getFullYear() + 1);
      return newDate;
    });
  };

  const handleDateClick = (day: number, month: number, year: number) => {
    const newDate = new Date(year, month, day);
    if (mode === 'single') {
      setSelectedDate(newDate);
      onChange?.(newDate);
      return;
    }

    if (!selectedRange.start || selectedRange.end) {
      setSelectedRange({ start: newDate, end: null });
    } else {
      const start = selectedRange.start;
      const end = newDate;
      const nextRange = end < start ? { start: end, end: start } : { start, end };
      setSelectedRange(nextRange);
      onChange?.(nextRange);
    }
  };

  const handleDoubleClick = (day: number, month: number, year: number) => {
    if (mode === 'single') {
      const newDate = new Date(year, month, day);
      setSelectedDate(newDate);
      onChange?.(newDate);
      onClose?.();
    } else {
      handleDateClick(day, month, year);
    }
  };

  const isSameDay = (left: Date | null, right: Date) =>
    !!left &&
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();
  const isDateInRange = (date: Date) =>
    mode === 'range' && !!selectedRange.start && !!selectedRange.end && date >= selectedRange.start && date <= selectedRange.end;
  const isDateStart = (date: Date) => mode === 'range' && isSameDay(selectedRange.start, date);
  const isDateEnd = (date: Date) => mode === 'range' && isSameDay(selectedRange.end, date);

  const handleMonthClick = () => {
    if (pickerMode === 'days') setView(view === 'days' ? 'years' : 'days');
    if (pickerMode === 'months') setView('months');
    if (pickerMode === 'years') setView('years');
  };

  const handleYearSelect = (year: number) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(year);
    newDate.setMonth(0);
    newDate.setDate(1);
    setCurrentDate(newDate);

    if (pickerMode === 'years') {
      setSelectedDate(newDate);
      onChange?.(year.toString());
      onClose?.();
    } else {
      setView('months');
    }
  };

  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(monthIndex);
    newDate.setDate(1);
    setCurrentDate(newDate);

    if (pickerMode === 'months') {
      setSelectedDate(newDate);
      onChange?.(newDate);
      onClose?.();
    } else {
      setView('days');
    }
  };

  const handleReset = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
    setSelectedRange({ start: null, end: null });
    onChange?.(null);
  };

  const handleSave = () => {
    if (mode === 'single') {
      onChange?.(pickerMode === 'years' ? selectedDate?.getFullYear()?.toString() || '' : selectedDate);
      onClose?.();
    } else if (selectedRange.start && selectedRange.end) {
      onChange?.({ start: selectedRange.start, end: selectedRange.end });
      onClose?.();
    }
  };

  const dayCellSizeClass = showLunar
    ? 'h-10 w-10 flex flex-col items-center justify-center leading-tight'
    : 'h-10 w-10 flex items-center justify-center';

  const renderDayLabel = (label: string | number, date: Date, isHighlighted: boolean) => {
    if (!showLunar) return label;
    return (
      <>
        <span className="text-xs sm:text-sm font-medium">{label}</span>
        <span className={cn('text-[9px] sm:text-[10px]', isHighlighted ? 'text-white/80' : 'text-gray-400')}>
          {getLunarDayLabel(date)}
        </span>
      </>
    );
  };

  const renderDateCell = (date: Date, label: string | number, key: string, isOutsideMonth = false) => {
    const day = startOfDay(date);
    const minDay = minDate ? startOfDay(minDate) : undefined;
    const maxDay = maxDate ? startOfDay(maxDate) : undefined;
    const isDisabled = (minDay ? day < minDay : false) || (maxDay ? day > maxDay : false) || (disabled?.(date) ?? false);
    const isToday = isSameDay(new Date(), date);
    const isSelected = mode === 'single' && isSameDay(selectedDate, date);
    const isInRange = mode === 'range' && isDateInRange(date);
    const isStart = mode === 'range' && isDateStart(date);
    const isEnd = mode === 'range' && isDateEnd(date);

    return (
      <div
        key={key}
        className={cn(
          'relative',
          mode === 'range' && isStart && "before:content-[''] before:absolute before:left-[50%] before:top-0 before:bottom-0 before:right-0 before:bg-[#fff7e6] before:z-0",
          mode === 'range' && isEnd && "before:content-[''] before:absolute before:right-[50%] before:top-0 before:bottom-0 before:left-0 before:bg-[#fff7e6] before:z-0",
        )}
        onClick={() => !isDisabled && handleDoubleClick(date.getDate(), date.getMonth(), date.getFullYear())}
      >
        <div
          className={cn(
            'mx-auto text-center text-[14px] cursor-pointer transition-colors duration-150 rounded-xl',
            dayCellSizeClass,
            isOutsideMonth && 'text-gray-400',
            !isDisabled && 'hover:bg-[#fff7e6] hover:text-[#f06f24]',
            isToday && !isSelected && !isStart && !isEnd && 'bg-gray-100 text-gray-900',
            mode === 'single' && isSelected && 'bg-[#f06f24] text-white shadow-sm hover:bg-[#f06f24] hover:text-white',
            mode === 'range' && (isStart || isEnd) && 'bg-[#f06f24] text-white relative z-10 hover:bg-[#f06f24] hover:text-white',
            mode === 'range' && isInRange && !isStart && !isEnd && 'bg-[#fff7e6] rounded-none',
            isDisabled && 'text-gray-300 cursor-not-allowed hover:bg-transparent hover:text-gray-300',
          )}
        >
          {renderDayLabel(label, date, (mode === 'single' && isSelected) || (mode === 'range' && (isStart || isEnd)))}
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevMonthYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevMonthYear, prevMonth);
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextMonthYear = month === 11 ? year + 1 : year;
    const days: React.ReactNode[] = [];

    for (let i = 0; i < firstDay; i++) {
      const day = daysInPrevMonth - firstDay + i + 1;
      days.push(renderDateCell(new Date(prevMonthYear, prevMonth, day), day, `prev-${i}`, true));
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(renderDateCell(new Date(year, month, i), String(i).padStart(2, '0'), `current-${i}`));
    }
    for (let i = 1; i <= 35 - days.length; i++) {
      days.push(renderDateCell(new Date(nextMonthYear, nextMonth, i), i, `next-${i}`, true));
    }

    return days;
  };

  const renderMonths = () => {
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    return monthNamesLong.map((month, index) => {
      const isDisabled =
        (minDate && currentYear === minDate.getFullYear() && index < minDate.getMonth()) ||
        (maxDate && currentYear === maxDate.getFullYear() && index > maxDate.getMonth()) ||
        (minDate && currentYear < minDate.getFullYear()) ||
        (maxDate && currentYear > maxDate.getFullYear());

      return (
        <div
          key={index}
          className={cn(
            'p-3 text-center cursor-pointer hover:bg-[#fff7e6] hover:text-[#f06f24] rounded-xl',
            currentMonth === index && 'bg-[#f06f24] text-white font-medium hover:bg-[#f06f24] hover:text-white',
            isDisabled && 'text-gray-300 cursor-not-allowed',
          )}
          onClick={() => !isDisabled && handleMonthSelect(index)}
        >
          {month}
        </div>
      );
    });
  };

  const renderYears = () => {
    const years: React.ReactNode[] = [];
    const currentYear = currentDate.getFullYear();

    for (let i = 0; i < 12; i++) {
      const year = yearRangeStart + i;
      const isDisabled = (minDate && year < minDate.getFullYear()) || (maxDate && year > maxDate.getFullYear());
      years.push(
        <div
          key={i}
          className={cn(
            'p-3 text-center cursor-pointer hover:bg-[#fff7e6] hover:text-[#f06f24] rounded-xl',
            currentYear === year && (pickerMode === 'days' || pickerMode === 'months') && 'bg-[#f06f24] text-white font-medium hover:bg-[#f06f24] hover:text-white',
            isDisabled && 'text-gray-300 cursor-not-allowed',
            value && (value as Date)?.getFullYear?.() === year && pickerMode === 'years' && 'bg-[#f06f24] text-white font-medium',
          )}
          onClick={() => !isDisabled && handleYearSelect(year)}
        >
          {year}
        </div>,
      );
    }

    return years;
  };

  const renderHeader = () => {
    const title =
      view === 'days'
        ? format(currentDate, 'MMMM yyyy', { locale })
        : view === 'months'
          ? format(currentDate, 'yyyy', { locale })
          : `${yearRangeStart} - ${yearRangeStart + 11}`;

    return (
      <div className="flex items-center justify-between gap-3 px-1 pb-3">
        <div className="flex items-center gap-1">
          {view === 'days' && (
            <button type="button" onClick={handlePreviousYear} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900" aria-label="Previous year">
              <ChevronsLeft className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={handlePrevious} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900" aria-label="Previous">
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
        <button type="button" className="min-w-[132px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-center text-sm font-semibold text-gray-900 hover:border-[#f06f24]/40 hover:bg-[#fff7e6] hover:text-[#f06f24]" onClick={handleMonthClick}>
          {title}
        </button>
        <div className="flex items-center gap-1">
          <button type="button" onClick={handleNext} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900" aria-label="Next">
            <ChevronRight className="h-4 w-4" />
          </button>
          {view === 'days' && (
            <button type="button" onClick={handleNextYear} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900" aria-label="Next year">
              <ChevronsRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={cn('w-[324px] rounded-xl border border-gray-100 bg-white p-3 text-sm text-gray-900 shadow-[0_12px_32px_rgba(17,24,39,0.14)]', className)}>
      {renderHeader()}
      {view === 'days' && (
        <>
          <div className="mb-1 grid grid-cols-7 rounded-lg bg-gray-50 px-1 py-1">
            {dayNames.map((day, index) => (
              <div key={index} className="text-center text-xs font-semibold text-gray-600">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-1">{renderDays()}</div>
        </>
      )}
      {view === 'months' && <div className="grid grid-cols-3 gap-2 p-1">{renderMonths()}</div>}
      {view === 'years' && <div className="grid grid-cols-3 gap-2 p-1">{renderYears()}</div>}
      {(isShowBtnReset || (pickerMode === 'days' && isShowBtnFooter)) && <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
        {isShowBtnReset ? (
          <button type="button" className="inline-flex h-9 items-center gap-2 rounded-lg border-0 bg-transparent px-2 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900" onClick={handleReset}>
            <RotateCcw className="w-4 h-4" /> {t('common.reset', 'Reset')}
          </button>
        ) : (
          <span />
        )}
        {pickerMode === 'days' && isShowBtnFooter && (
          <div className="flex items-center gap-2">
            <button type="button" className="h-9 rounded-lg border-0 bg-gray-100 px-4 text-sm text-gray-700 hover:bg-gray-200" onClick={onClose}>
              {t('b2c.txt.close', 'Close')}
            </button>
            <button type="button" className="h-9 rounded-lg border-0 bg-[#f06f24] px-4 text-sm font-semibold text-white shadow-sm hover:brightness-105" onClick={handleSave}>
              {t('common.save', 'Save')}
            </button>
          </div>
        )}
      </div>}
    </div>
  );
}
